// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';

// Brokerage-scope Quote Terms (provider rate cards) for the Advisor UI Provider
// Settings tab. Backed by /api/quotes/terms/** (tenant via X-Brokerage-Id, which
// advisorApi injects). Writes require Brokerage Admin. See quotes-engine-prd.md §7.2.

export interface QuoteTermAllocationBand {
  id?: string;
  contributionMin?: number | null;
  contributionMax?: number | null;
  allocationRate: number;
  sortOrder?: number;
}

export interface QuoteTermOption {
  id?: string;
  optionCode: string;
  optionName?: string | null;
  sortOrder?: number;
  allocationBasis: 'GROSS' | 'NET' | 'FLAT';
  flatAllocationRate?: number | null;
  standardFundCharge?: number | null;
  passiveFundCharge?: number | null;
  fixedAllocationFundCharge?: number | null;
  initialCommissionMin?: number | null;
  initialCommissionMax?: number | null;
  initialCommissionFixed?: number | null;
  fundBasedCommissionMin?: number | null;
  fundBasedCommissionMax?: number | null;
  accrualRate?: string | null;
  minContribution?: number | null;
  maxContribution?: number | null;
  minTermYears?: number | null;
  exitCharges?: number[];
  commissionClawback?: number[];
  policyFeeAmount?: number | null;
  policyFeeFrequency?: string | null;
  fundSwitches?: string | null;
  customerWithdrawals?: string | null;
  maxNraForCommission?: number | null;
  metadata?: Record<string, unknown> | null;
  bands?: QuoteTermAllocationBand[];
}

export interface QuoteTerm {
  id?: string;
  scope?: string;
  brokerageId?: string | null;
  providerId: string;
  providerName?: string;
  productId: string;
  productName?: string;
  category?: string;
  currencyCode: string;
  isActive?: boolean;
  notes?: string | null;
  options: QuoteTermOption[];
  createdAt?: string;
  updatedAt?: string;
}

export const quoteTermApi = {
  list: async (providerId: string): Promise<QuoteTerm[]> => {
    const response = await api.get<QuoteTerm[]>(`/quotes/terms/provider/${providerId}`);
    return response.data;
  },
  // Read-only PLATFORM default cards this brokerage inherits, shown badged "Platform".
  listPlatform: async (providerId: string): Promise<QuoteTerm[]> => {
    const response = await api.get<QuoteTerm[]>(`/quotes/terms/provider/${providerId}/platform`);
    return response.data;
  },
  get: async (termId: string): Promise<QuoteTerm> => {
    const response = await api.get<QuoteTerm>(`/quotes/terms/${termId}`);
    return response.data;
  },
  create: async (body: QuoteTerm): Promise<QuoteTerm> => {
    const response = await api.post<QuoteTerm>('/quotes/terms', body);
    return response.data;
  },
  update: async (termId: string, body: QuoteTerm): Promise<QuoteTerm> => {
    const response = await api.put<QuoteTerm>(`/quotes/terms/${termId}`, body);
    return response.data;
  },
  activate: async (termId: string): Promise<QuoteTerm> => {
    const response = await api.post<QuoteTerm>(`/quotes/terms/${termId}/activate`);
    return response.data;
  },
  deactivate: async (termId: string): Promise<QuoteTerm> => {
    const response = await api.post<QuoteTerm>(`/quotes/terms/${termId}/deactivate`);
    return response.data;
  },
  remove: async (termId: string): Promise<void> => {
    await api.delete(`/quotes/terms/${termId}`);
  },
};
